import React, { useCallback, useEffect, useState } from "react";
import { leaveSummaryTitle } from "../../Constant/title";
import { leaveSummaryEnums } from "../../Constant/HR/leaveSummary";
import BaseModal from "../../BaseComponents/BaseModal";
import { ChevronDown, ChevronUp, Calendar, AlertCircle } from "react-feather";
import { notFound, Submit } from "../../Constant";
import {
  encashLeaveApi,
  listOfLeaveSummaryApi,
  perDaySalaryApi,
  viewEncashLeaveHistoryApi,
} from "../../Api/leaveSummaryApi";
import {
  checkStatusCodeSuccess,
  errHandler,
  getFinancialYear,
  getItem,
  getLocalItem,
  handleResponse,
  isUserProfile,
  moduleName,
  RupeesSymbol,
} from "../../Constant/common";
import { useNavigate, useParams } from "react-router-dom";
import { usePageLoader } from "../../_helper/loading/LoadingContext";
import BaseInput from "../../BaseComponents/BaseInput";
import BaseSelect from "../../BaseComponents/BaseSelect";
import BaseButton from "../../BaseComponents/BaseButton";
import BackButton from "../../BaseComponents/BackButton";
import { toast } from "react-toastify";
import { HR } from "../../Constant/hrLayout";

const STATUS_STYLES = {
  approved: { bg: "rgba(16,185,129,0.12)", color: "#10b981" },
  pending: { bg: "rgba(245,158,11,0.12)", color: "#f59e0b" },
  rejected: { bg: "rgba(244,63,94,0.12)", color: "#f43f5e" },
};

const LeaveSummary = () => {
  const [expandedSections, setExpandedSections] = useState({});
  const { setLoading } = usePageLoader();
  const [btnLoader, setBtnLoader] = useState(false);
  const [leaveData, setLeaveData] = useState([]);
  const [leaveCount, setLeaveCount] = useState(0);
  const [salary, setSalary] = useState("");
  const [originalSalary, setOriginalSalary] = useState(0);
  const [modal, setModal] = useState(false);
  const [type, setType] = useState(null);
  const { staffId } = useParams();
  const [encashHistory, setEncashHistory] = useState([]);
  const [leaveBalances, setLeaveBalances] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("");
  const [enteredLeaveCount, setEnteredLeaveCount] = useState("");
  const [validationError, setValidationError] = useState("");
  const currentDate = new Date();
  const financialYear = getFinancialYear(currentDate);
  const navigate = useNavigate();
  const isProfile = JSON.parse(getItem(isUserProfile) || false);
  const profileId = getLocalItem("id");
  const finalId = isProfile ? profileId : staffId;
  const permission = JSON.parse(getLocalItem("permissions"));
  const hasLeaveSummary = permission?.some(
    (p) => p.parent_module_name === HR && p.module_name === moduleName.LeaveSummary,
  );

  useEffect(() => {
    document.title = leaveSummaryTitle;
  }, []);

  const fetchAllData = useCallback(() => {
    setLoading(true);
    Promise.all([
      listOfLeaveSummaryApi(finalId, {})
        .then((res) => {
          if (checkStatusCodeSuccess(res?.statusCode)) {
            const { totalLeavesTaken, totalRemainingLeave, ...months } = res?.data || {};
            setLeaveData(
              Object.entries(months).map(([month, leaves]) => ({
                month,
                totalDays: leaves[0]?.leaveCount || 0,
                details: leaves.slice(1),
              })),
            );
            setLeaveCount({ totalLeave: totalLeavesTaken, leaveBalance: totalRemainingLeave || 0 });
          } else setLeaveData([]);
        })
        .catch(() => setLeaveData([])),
      viewEncashLeaveHistoryApi(finalId)
        .then((res) => {
          const responseData = res?.data || res;
          if (responseData && typeof responseData === "object") {
            if (responseData.encashHistory && Array.isArray(responseData.encashHistory)) {
              setEncashHistory(responseData.encashHistory);
            } else if (Array.isArray(responseData)) {
              setEncashHistory(responseData);
            }
            if (responseData.leaveBalances && Array.isArray(responseData.leaveBalances)) {
              setLeaveBalances(responseData.leaveBalances);
            }
          }
        })
        .catch(() => {
          setEncashHistory([]);
          setLeaveBalances([]);
        }),
    ]).finally(() => setLoading(false));
  }, [finalId, setLoading]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    fetchAllData();
  }, [fetchAllData]);

  const toggleDetails = (month) =>
    setExpandedSections((prev) => ({ ...prev, [month]: !prev[month] }));

  const filteredBalances = React.useMemo(() => {
    return (
      leaveBalances?.filter(
        (b) => b.leave_category_name?.trim()?.toLowerCase() !== "unpaid leave",
      ) || []
    );
  }, [leaveBalances]);

  const totalYearEndBalance = React.useMemo(() => {
    return filteredBalances.reduce((sum, item) => sum + (Number(item?.year_end_balance) || 0), 0);
  }, [filteredBalances]);

  const selectedBalanceObj = React.useMemo(() => {
    return filteredBalances.find((b) => b.leave_category_name === selectedCategory) || null;
  }, [filteredBalances, selectedCategory]);

  useEffect(() => {
    if (modal && filteredBalances?.length > 0 && !selectedCategory) {
      setSelectedCategory(filteredBalances[0].leave_category_name);
    }
  }, [modal, filteredBalances, selectedCategory]);

  useEffect(() => {
    if (!selectedCategory) {
      setValidationError("");
      return;
    }
    const yearEndBalance = selectedBalanceObj?.year_end_balance || 0;
    const count = Number(enteredLeaveCount);

    if (enteredLeaveCount === "") {
      setValidationError("");
      return;
    }

    if (isNaN(count) || count <= 0) {
      setValidationError("Leave count must be greater than 0");
    } else if (count > yearEndBalance) {
      setValidationError(`Cannot encash more than last year's balance (${yearEndBalance} leaves)`);
    } else {
      setValidationError("");
    }
  }, [enteredLeaveCount, selectedCategory, selectedBalanceObj]);

  const handleSelectAction = (action) => {
    setType(action);
    if (action === leaveSummaryEnums.encashLeave) {
      setLoading(true);
      setModal(true);
      setSelectedCategory("");
      setEnteredLeaveCount("");
      setValidationError("");
      perDaySalaryApi(finalId)
        .then((res) => {
          if (checkStatusCodeSuccess(res?.statusCode)) {
            setSalary(res?.data);
            setOriginalSalary(res?.data);
          }
        })
        .catch((err) => err)
        .finally(() => setLoading(false));
    }
  };

  const handleSubmit = () => {
    setBtnLoader(true);
    encashLeaveApi(finalId, {
      leave_count: Number(enteredLeaveCount),
      leave_category_name: selectedCategory,
      amount_per_leave: Number(salary || 0),
    })
      .then((res) => {
        if (checkStatusCodeSuccess(res?.statusCode)) {
          toast.success(res?.message);
          setModal(false);
          fetchAllData();
        } else toast.error(res?.message);
      })
      .catch(errHandler)
      .finally(() => setBtnLoader(false));
  };

  useEffect(() => {
    if (!modal) {
      setSelectedCategory("");
      setEnteredLeaveCount("");
      setValidationError("");
      setSalary("");
      setOriginalSalary(0);
    }
  }, [modal]);

  return (
    <div className="animate-fade-in">
      {/* Header */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 20,
          flexWrap: "wrap",
          gap: 12,
        }}
      >
        <div>
          <h2
            style={{
              fontSize: "1.125rem",
              fontWeight: 800,
              margin: 0,
              color: "var(--text-primary)",
            }}
          >
            {leaveSummaryEnums.leaveSummary}
          </h2>
          <p style={{ margin: 0, fontSize: "0.8125rem", color: "var(--text-muted)" }}>
            {financialYear}
          </p>
        </div>
        <div style={{ display: "flex", gap: 10 }}>
          {hasLeaveSummary && (
            <BaseButton
              color="primary"
              onClick={() => handleSelectAction(leaveSummaryEnums.encashLeave)}
            >
              {leaveSummaryEnums.encashLeave}
            </BaseButton>
          )}
          <BackButton
            onClick={() =>
              isProfile ? navigate("/profile") : navigate(`/staffProfile/${staffId}`)
            }
          />
        </div>
      </div>

      {/* Stats */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
          gap: 12,
          marginBottom: 20,
        }}
      >
        {[
          {
            label: leaveSummaryEnums.totalLeavesTaken,
            value: leaveCount?.totalLeave ?? handleResponse.nullData,
            color: "#f59e0b",
          },
          {
            label: leaveSummaryEnums.balanceLeave,
            value: leaveCount?.leaveBalance ?? handleResponse.nullData,
            color: "#10b981",
          },
          {
            label: "Last Year Balance",
            value: totalYearEndBalance ?? 0,
            color: "var(--brand-indigo)",
          },
        ].map(({ label, value, color }) => (
          <div
            key={label}
            style={{
              background: "var(--glass-bg)",
              border: "1px solid var(--glass-border)",
              borderRadius: 12,
              padding: "16px 20px",
            }}
          >
            <div
              style={{
                fontSize: "0.75rem",
                fontWeight: 600,
                color: "var(--text-muted)",
                textTransform: "uppercase",
                letterSpacing: "0.08em",
                marginBottom: 6,
              }}
            >
              {label}
            </div>
            <div style={{ fontSize: "1.5rem", fontWeight: 800, color }}>{value}</div>
          </div>
        ))}
      </div>

      {/* Leave Balances */}
      <div style={{ marginBottom: 20 }}>
        <h3
          style={{
            fontSize: "1rem",
            fontWeight: 800,
            marginBottom: 12,
            color: "var(--text-primary)",
          }}
        >
          Leave Balances
        </h3>
        {filteredBalances?.length > 0 ? (
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
              gap: 16,
            }}
          >
            {filteredBalances.map((item, idx) => (
              <div
                key={idx}
                style={{
                  background: "var(--glass-bg)",
                  border: "1px solid var(--glass-border)",
                  borderRadius: 12,
                  padding: "16px 20px",
                  display: "flex",
                  flexDirection: "column",
                  gap: 10,
                }}
              >
                <div
                  style={{ fontSize: "0.9375rem", fontWeight: 700, color: "var(--brand-indigo)" }}
                >
                  {item.leave_category_name}
                </div>
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 6,
                    fontSize: "0.8125rem",
                  }}
                >
                  <div style={{ display: "flex", justifyContent: "space-between" }}>
                    <span style={{ color: "var(--text-secondary)" }}>
                      Last year unused balance:
                    </span>
                    <span
                      style={{
                        fontWeight: 700,
                        color:
                          item.year_end_balance > 0 ? "var(--text-primary)" : "var(--text-muted)",
                      }}
                    >
                      {item.year_end_balance} leaves
                    </span>
                  </div>
                  {item.year_end_balance === 0 && (
                    <div style={{ color: "var(--danger)", fontSize: "0.75rem", fontWeight: 600 }}>
                      No leaves available to encash from last year.
                    </div>
                  )}
                  <div style={{ display: "flex", justifyContent: "space-between" }}>
                    <span style={{ color: "var(--text-secondary)" }}>Current year balance:</span>
                    <span style={{ fontWeight: 600, color: "var(--text-primary)" }}>
                      {item.remaining_leave} leaves
                    </span>
                  </div>
                  <div style={{ display: "flex", justifyContent: "space-between" }}>
                    <span style={{ color: "var(--text-secondary)" }}>Carry forward limit:</span>
                    <span style={{ fontWeight: 600, color: "var(--text-primary)" }}>
                      {item.carry_forward_limit} leaves
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              padding: "20px",
              gap: 8,
              background: "var(--glass-bg)",
              border: "1px solid var(--glass-border)",
              borderRadius: 12,
            }}
          >
            <p style={{ color: "var(--text-muted)", margin: 0, fontSize: "0.8125rem" }}>
              No leave balances found
            </p>
          </div>
        )}
      </div>

      {/* Leave accordion */}
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {leaveData?.length > 0 ? (
          leaveData.map((section, i) => {
            const isOpen = expandedSections[section?.month];
            return (
              <div
                key={i}
                style={{
                  background: "var(--glass-bg)",
                  border: "1px solid var(--glass-border)",
                  borderRadius: 12,
                  overflow: "hidden",
                }}
              >
                <div
                  onClick={() => toggleDetails(section?.month)}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    padding: "14px 18px",
                    cursor: "pointer",
                    borderBottom: isOpen ? "1px solid var(--glass-border)" : "none",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = "var(--nav-hover)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = "transparent";
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <Calendar
                      size={16}
                      strokeWidth={2}
                      style={{ color: "var(--brand-indigo)", flexShrink: 0 }}
                    />
                    <span
                      style={{
                        fontWeight: 700,
                        fontSize: "0.9375rem",
                        color: "var(--text-primary)",
                      }}
                    >
                      {section?.month}
                    </span>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                    <span
                      style={{
                        padding: "3px 10px",
                        borderRadius: 999,
                        fontSize: "0.75rem",
                        fontWeight: 700,
                        background: "rgba(99,102,241,0.1)",
                        color: "var(--brand-indigo)",
                      }}
                    >
                      {section?.totalDays}{" "}
                      {Number(section?.totalDays) > 1
                        ? leaveSummaryEnums.days
                        : leaveSummaryEnums.day}
                    </span>
                    {isOpen ? (
                      <ChevronUp size={16} style={{ color: "var(--text-muted)" }} />
                    ) : (
                      <ChevronDown size={16} style={{ color: "var(--text-muted)" }} />
                    )}
                  </div>
                </div>

                {isOpen && (
                  <div style={{ padding: "8px 0" }} className="animate-fade-in">
                    {section?.details?.map((detail, idx) => {
                      const status = detail?.leave_status?.toLowerCase();
                      const s = STATUS_STYLES[status] || STATUS_STYLES.rejected;
                      return (
                        <div
                          key={idx}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            padding: "10px 18px",
                            flexWrap: "wrap",
                            gap: 8,
                            borderBottom:
                              idx < section.details.length - 1
                                ? "1px solid var(--glass-border)"
                                : "none",
                          }}
                        >
                          <div style={{ fontSize: "0.8125rem", color: "var(--text-primary)" }}>
                            {detail?.date} &nbsp;·&nbsp;
                            <span style={{ color: "var(--text-muted)" }}>
                              {detail?.leave_duration}{" "}
                              {Number(detail?.leave_duration) > 1
                                ? leaveSummaryEnums.days
                                : leaveSummaryEnums.day}
                            </span>
                          </div>
                          <div style={{ fontSize: "0.8125rem", color: "var(--text-secondary)" }}>
                            {detail?.leave_name}
                          </div>
                          <span
                            style={{
                              padding: "3px 10px",
                              borderRadius: 999,
                              fontSize: "0.6875rem",
                              fontWeight: 700,
                              background: s.bg,
                              color: s.color,
                              textTransform: "capitalize",
                            }}
                          >
                            {detail?.leave_status}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })
        ) : (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              padding: "60px 20px",
              gap: 12,
              background: "var(--glass-bg)",
              border: "1px solid var(--glass-border)",
              borderRadius: 12,
            }}
          >
            <AlertCircle size={40} style={{ color: "var(--text-muted)", opacity: 0.4 }} />
            <p style={{ color: "var(--text-muted)", margin: 0 }}>{notFound.dataNotFound}</p>
          </div>
        )}
      </div>

      {/* Leave Encashment History */}
      <div style={{ marginTop: 24 }}>
        <h3
          style={{
            fontSize: "1rem",
            fontWeight: 800,
            marginBottom: 12,
            color: "var(--text-primary)",
          }}
        >
          Leave Encashment History
        </h3>
        {encashHistory?.length > 0 ? (
          <div
            style={{
              background: "var(--glass-bg)",
              border: "1px solid var(--glass-border)",
              borderRadius: 12,
              overflow: "hidden",
            }}
          >
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.8125rem" }}>
                <thead>
                  <tr
                    style={{
                      borderBottom: "1px solid var(--glass-border)",
                      background: "rgba(99,102,241,0.04)",
                    }}
                  >
                    <th
                      style={{
                        padding: "12px 18px",
                        textAlign: "left",
                        color: "var(--text-secondary)",
                        fontWeight: 700,
                      }}
                    >
                      Date
                    </th>
                    <th
                      style={{
                        padding: "12px 18px",
                        textAlign: "right",
                        color: "var(--text-secondary)",
                        fontWeight: 700,
                      }}
                    >
                      Leaves Encashed
                    </th>
                    <th
                      style={{
                        padding: "12px 18px",
                        textAlign: "right",
                        color: "var(--text-secondary)",
                        fontWeight: 700,
                      }}
                    >
                      Amount Per Leave
                    </th>
                    <th
                      style={{
                        padding: "12px 18px",
                        textAlign: "right",
                        color: "var(--text-secondary)",
                        fontWeight: 700,
                      }}
                    >
                      Total Payout
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {encashHistory.map((item, idx) => (
                    <tr
                      key={item.id || idx}
                      style={{
                        borderBottom:
                          idx < encashHistory.length - 1 ? "1px solid var(--glass-border)" : "none",
                      }}
                    >
                      <td style={{ padding: "12px 18px", color: "var(--text-primary)" }}>
                        {item.date}
                      </td>
                      <td
                        style={{
                          padding: "12px 18px",
                          textAlign: "right",
                          color: "var(--text-primary)",
                          fontWeight: 600,
                        }}
                      >
                        {item.leave_count}
                      </td>
                      <td
                        style={{
                          padding: "12px 18px",
                          textAlign: "right",
                          color: "var(--text-primary)",
                        }}
                      >
                        {RupeesSymbol(Number(item.amount_per_leave || 0).toFixed(2))}
                      </td>
                      <td
                        style={{
                          padding: "12px 18px",
                          textAlign: "right",
                          color: "var(--brand-indigo)",
                          fontWeight: 700,
                        }}
                      >
                        {RupeesSymbol(Number(item.total_payout || 0).toFixed(2))}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              padding: "30px 20px",
              gap: 8,
              background: "var(--glass-bg)",
              border: "1px solid var(--glass-border)",
              borderRadius: 12,
            }}
          >
            <AlertCircle size={24} style={{ color: "var(--text-muted)", opacity: 0.4 }} />
            <p style={{ color: "var(--text-muted)", margin: 0, fontSize: "0.8125rem" }}>
              No encashment history found
            </p>
          </div>
        )}
      </div>

      {/* Encash modal */}
      <BaseModal
        isOpen={modal}
        title={type}
        toggler={() => setModal(false)}
        submit={handleSubmit}
        submitText={Submit}
        disabled={
          btnLoader ||
          !enteredLeaveCount ||
          !!validationError ||
          !selectedCategory ||
          (selectedBalanceObj && selectedBalanceObj.year_end_balance <= 0) ||
          salary === "" ||
          Number(salary) < 0
        }
        loader={btnLoader}
      >
        {filteredBalances?.length > 0 && (
          <BaseSelect
            label="Leave Category"
            name="leave_category"
            options={filteredBalances.map((b) => ({
              value: b.leave_category_name,
              label: b.leave_category_name,
            }))}
            value={selectedCategory}
            handleChange={(name, val) => setSelectedCategory(val)}
            placeholder="Select Leave Category"
            required
          />
        )}
        {selectedBalanceObj && (
          <div
            style={{
              padding: "12px 16px",
              background: "var(--icon-btn-bg)",
              borderRadius: 10,
              marginBottom: 16,
              display: "flex",
              flexDirection: "column",
              gap: 8,
              fontSize: "0.8125rem",
            }}
          >
            <div
              style={{
                fontWeight: 700,
                color: "var(--brand-indigo)",
                fontSize: "0.875rem",
                marginBottom: 4,
              }}
            >
              {selectedBalanceObj.leave_category_name}
            </div>
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <span style={{ color: "var(--text-secondary)" }}>Last year unused balance:</span>
              <span
                style={{
                  fontWeight: 700,
                  color:
                    selectedBalanceObj.year_end_balance > 0
                      ? "var(--text-primary)"
                      : "var(--text-muted)",
                }}
              >
                {selectedBalanceObj.year_end_balance} leaves
              </span>
            </div>
            {selectedBalanceObj.year_end_balance === 0 && (
              <div style={{ color: "var(--danger)", fontSize: "0.75rem", fontWeight: 600 }}>
                No leaves available to encash from last year.
              </div>
            )}
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <span style={{ color: "var(--text-secondary)" }}>Current year balance:</span>
              <span style={{ fontWeight: 600, color: "var(--text-primary)" }}>
                {selectedBalanceObj.remaining_leave} leaves
              </span>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <span style={{ color: "var(--text-secondary)" }}>Carry forward limit:</span>
              <span style={{ fontWeight: 600, color: "var(--text-primary)" }}>
                {selectedBalanceObj.carry_forward_limit} leaves
              </span>
            </div>
          </div>
        )}
        <BaseInput
          name="entered_leave_count"
          type="number"
          label={leaveSummaryEnums.selectEncashleave}
          value={enteredLeaveCount}
          handleChange={(e) => setEnteredLeaveCount(e.target.value)}
          error={validationError}
          touched={!!validationError}
          disabled={!selectedBalanceObj || selectedBalanceObj.year_end_balance <= 0}
        />
        <p style={{ fontSize: "0.8125rem", color: "var(--text-muted)", margin: "8px 0" }}>
          {leaveSummaryEnums.leaveInstruction}
        </p>
        <BaseInput
          name="amount_per_leave"
          type="number"
          label={leaveSummaryEnums.amountPerLeave}
          value={salary}
          handleChange={(e) => setSalary(e.target.value)}
          icon="₹"
        />
        <button
          type="button"
          onClick={() => setSalary((prev) => (Number(prev) === 0 ? originalSalary : 0))}
          style={{
            background: "none",
            border: "none",
            cursor: "pointer",
            color: "var(--brand-indigo)",
            fontSize: "0.8125rem",
            fontWeight: 600,
            padding: 0,
            marginBottom: 12,
          }}
        >
          {leaveSummaryEnums.reset}
        </button>
        <div
          style={{
            padding: "12px 16px",
            background: "rgba(99,102,241,0.06)",
            border: "1px solid rgba(99,102,241,0.15)",
            borderRadius: 10,
          }}
        >
          <div style={{ fontSize: "0.8125rem", color: "var(--text-muted)", marginBottom: 4 }}>
            {leaveSummaryEnums.totalPayout}
          </div>
          <div style={{ fontSize: "1rem", fontWeight: 800, color: "var(--brand-indigo)" }}>
            {Number(enteredLeaveCount || 0)} × {Number(salary || 0)} ={" "}
            {RupeesSymbol((Number(enteredLeaveCount || 0) * Number(salary || 0)).toFixed(2))}
          </div>
          {selectedBalanceObj && selectedBalanceObj.year_end_balance <= 0 && (
            <div style={{ color: "var(--danger)", fontSize: "0.75rem", marginTop: 6 }}>
              {leaveSummaryEnums.noLeaveMsg}
            </div>
          )}
        </div>
      </BaseModal>
    </div>
  );
};

export default LeaveSummary;
